'use client';
import { toast } from 'sonner';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { Credits } from '@/components/Credits';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import { useNavigation } from '@/contexts/NavigationContext';
import { useClaimSheetStore } from '@/stores/claimSheetStore';
import { playEarnings } from '@/lib/uiSounds';
import { useMyAchievements } from '../hooks/useMyAchievements';
import { useClaimableRewards, useClaimRewards } from '../hooks/useClaimableRewards';
import { pickTitle } from '../utils/pickLocale';
import { AchievementIcon } from './AchievementIcon';
import { AchievementClaimBannerView } from './AchievementClaimBanner';
import translations from './AchievementClaimBanner.translations';

/** App-level claim hub. Mounted once per runtime; opened via the claim-sheet
 *  store from the achievements banner, the trophy detail sheet on any screen,
 *  and the unlock toast. */
export function AchievementClaimSheet() {
  const { t } = useLocalTranslation(translations);
  const isOpen = useClaimSheetStore((s) => s.isOpen);
  const close = useClaimSheetStore((s) => s.close);

  return (
    <BottomSheet isOpen={isOpen} onClose={close} title={t('title')} heightClass="h-auto max-h-[85vh]">
      {isOpen && <ClaimSheetBody onClose={close} />}
    </BottomSheet>
  );
}

/** Body split out so the achievement queries only run while the sheet is open. */
function ClaimSheetBody({ onClose }: { onClose: () => void }) {
  const { t } = useLocalTranslation(translations);
  const { data } = useClaimableRewards();
  const { data: mine } = useMyAchievements();
  const claim = useClaimRewards();
  const navigation = useNavigation();

  const unclaimed = (mine?.items ?? []).filter((a) => a.unlockedAt && !a.rewardClaimed);

  const onClaim = () => {
    if (claim.isPending) return;
    claim.mutate(undefined, {
      onSuccess: (res) => {
        playEarnings();
        toast.success(t('claimed'), { description: `+${res.claimedCoins} ${t('coins')} · +${res.claimedXp} XP` });
        onClose();
      },
    });
  };

  const onDeposit = () => {
    close();
    navigation.navigate('payment');
  };

  return (
    <div className="space-y-4 px-4 pb-6">
      {data && data.count > 0 && (
        <AchievementClaimBannerView
          data={data}
          claiming={claim.isPending}
          onClaim={onClaim}
          onDeposit={onDeposit}
        />
      )}

      {unclaimed.length > 0 && (
        <div className="space-y-1.5">
          {unclaimed.map((a) => (
            <div key={a.key} className="flex items-center gap-3 rounded-xl bg-fill-tertiary/60 p-2">
              <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg">
                <AchievementIcon iconFile={a.iconFile} alt="" fill className="object-contain" />
              </div>
              <span className="min-w-0 flex-1 truncate text-sm text-label-primary">
                {pickTitle(a.i18n)}
              </span>
              {!!a.reward.credits && (
                <Credits
                  amount={a.reward.credits}
                  iconSize={11}
                  className="shrink-0 text-xs text-label-secondary"
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
